import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { categories } from '@/data/categories';
import { useClickOutside } from '@/hooks/useClickOutside';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const catRef = useClickOutside<HTMLDivElement>(() => setCatOpen(false));
  const langRef = useClickOutside<HTMLDivElement>(() => setLangOpen(false));

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  function changeLanguage(code: string) {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  }

  return (
    <header className="sticky top-0 z-50 border-b border-primary-200 bg-primary-50">
      <nav className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-table-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="font-heading text-base font-bold text-foreground-950">
              USFans <span className="text-primary-600">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden items-center gap-1 md:flex">
            <Link to="/" className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 hover:bg-primary-100 whitespace-nowrap cursor-pointer">
              {t('nav_home')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 hover:bg-primary-100 whitespace-nowrap cursor-pointer"
            >
              {t('nav_shopping_guide')}
              <i className="ri-external-link-line text-sm text-primary-600" aria-hidden="true"></i>
            </a>
            <Link to="/article/what-is-a-usfans-spreadsheet" className="rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 hover:bg-primary-100 whitespace-nowrap cursor-pointer">
              {t('nav_guide')}
            </Link>

            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 hover:bg-primary-100 whitespace-nowrap cursor-pointer"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-72 rounded-lg border border-primary-200 bg-background-50 p-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setCatOpen(false)}
                      className="flex items-center gap-3 rounded-md px-2 py-2 text-sm text-foreground-700 hover:bg-primary-50 cursor-pointer"
                    >
                      <span className="flex h-10 w-10 flex-none items-center justify-center overflow-hidden rounded-md bg-background-100">
                        <img src={cat.image} alt="" aria-hidden="true" className="h-full w-full object-cover object-top" />
                      </span>
                      <span className="flex-1 whitespace-nowrap">{t(`category_${cat.key}`)}</span>
                      <i className="ri-arrow-right-up-line text-primary-500" aria-hidden="true"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={langRef}>
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 rounded-md border border-primary-200 px-3 py-2 text-sm font-semibold text-foreground-800 hover:bg-primary-100 whitespace-nowrap cursor-pointer"
              >
                <span aria-hidden="true">{currentLang.flag}</span>
                {currentLang.label}
                <i className={`ri-arrow-down-s-line text-base transition-transform ${langOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 rounded-lg border border-primary-200 bg-background-50 p-1">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => changeLanguage(l.code)}
                      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap cursor-pointer ${
                        l.code === currentLang.code ? 'bg-primary-100 text-primary-700 font-semibold' : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <span aria-hidden="true">{l.flag}</span>
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:block">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap transition-colors cursor-pointer"
            >
              {t('btn_buy_now')}
            </a>
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 hover:bg-primary-100 md:hidden cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} aria-hidden="true"></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-primary-200 py-4 md:hidden">
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-primary-100 cursor-pointer">
                <i className="ri-home-4-line mr-2 text-primary-600" aria-hidden="true"></i>
                {t('nav_home')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-primary-100 cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line mr-2 text-primary-600" aria-hidden="true"></i>
                {t('nav_shopping_guide')}
              </a>
              <Link to="/article/what-is-a-usfans-spreadsheet" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-primary-100 cursor-pointer">
                <i className="ri-article-line mr-2 text-primary-600" aria-hidden="true"></i>
                {t('nav_guide')}
              </Link>
              <div className="my-2 border-t border-primary-200" />
              <p className="px-3 py-1 text-xs font-bold uppercase tracking-wide text-foreground-500">{t('nav_categories')}</p>
              <div className="grid grid-cols-2 gap-2 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 overflow-hidden rounded-lg border border-primary-200 bg-background-50 p-2 cursor-pointer"
                  >
                    <span className="flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-md bg-background-100">
                      <img src={cat.image} alt="" aria-hidden="true" className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="truncate text-xs font-medium text-foreground-700">{t(`category_${cat.key}`)}</span>
                  </a>
                ))}
              </div>
              <div className="my-2 border-t border-primary-200" />
              <p className="px-3 py-1 text-xs font-bold uppercase tracking-wide text-foreground-500">{t('lang_label')}</p>
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => changeLanguage(l.code)}
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-100 cursor-pointer"
                >
                  <span aria-hidden="true">{l.flag}</span>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}